import { Activity, useState, useCallback, useEffect, useRef } from 'react'
import { useNotifications } from '../../../../hooks/context/useNotifications'
import { useLocalization, LocalizationKeys } from '../../../../i18n'
import { getUnreadNotificationText } from '../../../../utils/notificationUtils'
import { NotificationItem } from '../notificationItem/notificationItem'
import styles from './notificationContainer.module.css'

export function NotificationContainer() {
  const { notifications, markAsRead, loadMore, fetchAll, isLoading, error } = useNotifications()
  const { t } = useLocalization()
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const unreadCount = notifications.filter(n => !n.isWatched).length

  // Загружаем полный список при открытии окна
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsRead([notificationId])
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  }, [markAsRead])

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadIds = notifications
      .filter(n => !n.isWatched)
      .map(n => n.id)
    
    if (unreadIds.length === 0) return

    try {
      await markAsRead(unreadIds)
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err)
    }
  }, [notifications, markAsRead])

  const handleScroll = useCallback(async () => {
    const container = scrollContainerRef.current
    if (!container || isLoadingMore || isLoading) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 50

    if (scrolledToBottom) {
      setIsLoadingMore(true)
      try {
        await loadMore()
      } catch (err) {
        console.error('Failed to load more notifications:', err)
      } finally {
        setIsLoadingMore(false)
      }
    }
  }, [isLoadingMore, isLoading, loadMore])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Показываем ошибку загрузки
  if (error) {
    return (
      <div
        className={styles.container}
        role="region"
        aria-label={t(LocalizationKeys.HEADER_NOTIFICATIONS)}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>
            {t(LocalizationKeys.HEADER_NOTIFICATIONS)}
          </h2>
        </header>
        <div className={styles.content}>
          <p className={styles.emptyState}>
            {t(LocalizationKeys.NOTIFICATION_LOAD_ERROR)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.container}
      role="region"
      aria-label={t(LocalizationKeys.HEADER_NOTIFICATIONS)}
    >
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <h2 className={styles.title}>
            {t(LocalizationKeys.HEADER_NOTIFICATIONS)}
          </h2>
          <Activity mode={unreadCount > 0 ? 'visible' : 'hidden'}>
            <button
              type="button"
              className={styles.markAllBtn}
              onClick={handleMarkAllAsRead}
            >
              {t(LocalizationKeys.NOTIFICATION_MARK_ALL_READ)}
            </button>
          </Activity>
        </div>
        <span className={styles.subtitle}>
          {getUnreadNotificationText(unreadCount, t)}
        </span>
      </header>
      
      <div className={styles.content} ref={scrollContainerRef}>
        {notifications.length === 0 ? (
          <p className={styles.emptyState}>
            {t(LocalizationKeys.NOTIFICATION_EMPTY_STATE)}
          </p>
        ) : (
          <>
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
            <Activity mode={isLoadingMore ? 'visible' : 'hidden'}>
              <div className={styles.loading}>
                {t(LocalizationKeys.NOTIFICATION_LOADING_MORE)}
              </div>
            </Activity>
          </>
        )}
      </div>
    </div>
  )
}
